/**
 * CV Health in the CV editor (web): ONE WebDialog (centred dialog on desktop,
 * bottom sheet at phone width) titled with the Health heart, whose body swaps
 * between the overview, an area's tabs, the change review and the one-link
 * view. The twin of `CvHealthSheet.tsx`; both render
 * `cv-health-sheet-controller.ts`.
 *
 * The overview is the 30 Sep 2026 design: the score card (gauge, then the
 * capsule "Review N suggestions" or "All checks passed"), the saved notice
 * with Undo, "View individual suggestions" (each row opens its editable
 * comparison) and "What we checked" (each audience opens its tab). The old
 * setup checklist has no row here (owner, 2026-10-04). While a review runs:
 * the four checking steps and a slim line of real progress.
 *
 * Keyboard: focus moves into the dialog on open and stays there (Tab wraps);
 * Escape steps back one view, and closes from the overview; focus returns to
 * the dock's CV Health control on close. Edited, unsaved link text asks
 * "Keep editing / Discard changes" through the in-app confirm (never
 * `window.confirm`). Opening the dialog never starts a review. No toasts.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WebDialog } from '../common/WebDialog.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { HealthAreaReport } from '../cv-health/HealthAreaReport.web';
import { HealthChangeReview } from '../cv-health/HealthChangeReview.web';
import { HealthCheckProgress } from '../cv-health/HealthCheckProgress.web';
import { HealthCheckedAreas } from '../cv-health/HealthCheckedAreas.web';
import { HealthDisclosure } from '../cv-health/HealthDisclosure.web';
import { revealSuggestions, SUGGESTIONS_TEST_ID } from '../cv-health/health-disclosure-memory';
import { HealthHeart } from '../cv-health/HealthHeart.web';
import { HealthProposalReview } from '../cv-health/HealthProposalReview.web';
import { HealthScoreCard } from '../cv-health/HealthScoreCard.web';
import { HealthStatusLine } from '../cv-health/HealthStatusLine.web';
import { HealthSuggestionList } from '../cv-health/HealthSuggestionList.web';
import type { HealthFindingActionProps, HealthSuggestionListProps } from '../cv-health/health-props';
import { getStageTextKey, getSuggestionFindings } from '../cv-health/cv-health-copy';
import { confirm } from '../../lib/alert.web';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';
import { isReportUsable } from '../../lib/cv-health-model';
import {
  useCvHealthSheetController,
  type CvHealthSaveError,
  type CvHealthSheetController,
} from './cv-health-sheet-controller';

const DOCK_CONTROL_SELECTOR = '.tabbio-health-dock';
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface CvHealthSheetProps {
  visible: boolean;
  onClose: () => void;
}

/** A quiet sentence under the card (skipped changes), or an inline error. */
function Note({ text, tone = 'quiet' }: { text: string; tone?: 'quiet' | 'error' }) {
  return tone === 'error' ? (
    <p className="tabbio-health-error" role="alert">
      {text}
    </p>
  ) : (
    <p className="tabbio-health-note" aria-live="polite">
      {text}
    </p>
  );
}

/**
 * "3 changes saved · Undo" under the card; "Checking your changes…" while the
 * recheck runs. A recheck that failed says so (the change is still saved) and
 * offers "Check again" unless the view already has that button; an Undo that
 * could not restore everything says how many stayed.
 */
function SavedNotice({
  controller,
  offerRecheck = true,
}: {
  controller: CvHealthSheetController;
  offerRecheck?: boolean;
}) {
  const { t } = useTranslation('seeker');
  const { t: tc } = useTranslation('common');
  const { receipt, recheckStatus } = controller;
  if (!receipt) return null;
  const checking = recheckStatus === 'checking';
  const recheckFailed = recheckStatus === 'failed';
  const kept = receipt.undone && receipt.undoSkipped > 0;
  const text = checking
    ? tc('cvHealth.report.checkingChanges')
    : kept
      ? tc('cvHealth.report.undoSkipped', { count: receipt.undoSkipped })
      : receipt.undone
        ? tc('cvHealth.report.undone')
        : recheckFailed
          ? tc('cvHealth.report.recheckFailed', { count: receipt.receipt.items.length })
          : tc('cvHealth.report.changesSaved', { count: receipt.receipt.items.length });
  return (
    <div className="tabbio-health-notice" role="status">
      {checking ? (
        <HealthHeart size={16} state="working" />
      ) : (
        <MaterialSymbol name={kept || recheckFailed ? 'info' : 'check'} size={16} />
      )}
      <span>{text}</span>
      {recheckFailed && offerRecheck ? (
        <button type="button" className="tabbio-health-btn is-text" onClick={controller.handleRecheck}>
          {t('cv.health.checkAgain')}
        </button>
      ) : null}
      {controller.canUndo ? (
        <button
          type="button"
          className="tabbio-health-btn is-text"
          onClick={() => {
            void controller.handleUndo();
          }}
          disabled={controller.undoing}
          aria-busy={controller.undoing || undefined}
        >
          {tc('cvHealth.simple.undo')}
        </button>
      ) : null}
    </div>
  );
}

function Overview({ controller }: { controller: CvHealthSheetController }) {
  const { t } = useTranslation('seeker');
  const { t: tc } = useTranslation('common');
  const { overviewMode, overviewReport, showsResult, checkingReport, stage, fixableFindings } =
    controller;
  const { suggestionsOpen, setSuggestionsOpen, checkedOpen, setCheckedOpen } = controller;

  if (!showsResult || !overviewReport) {
    if (overviewMode === 'checking') {
      return (
        <>
          <HealthCheckProgress report={checkingReport} stage={stage} />
        </>
      );
    }
    if (overviewMode === 'empty' || overviewMode === 'failed') {
      const failed = overviewMode === 'failed';
      // After a save, the saved notice already says the recheck failed; the
      // generic "couldn't review this CV" line would contradict "N saved".
      const savedRecheckFailed =
        failed && controller.receipt !== null && controller.recheckStatus === 'failed';
      return (
        <>
          {savedRecheckFailed ? null : (
            <HealthStatusLine
              text={failed ? tc(getStageTextKey('failed')) : t('cv.health.introNoReport')}
              working={false}
              align="start"
            />
          )}
          {/* A saved change whose recheck failed is still saved, and Undo still works. */}
          {failed ? <SavedNotice controller={controller} offerRecheck={false} /> : null}
          <button
            type="button"
            className="tabbio-health-btn is-primary is-block"
            onClick={failed ? controller.handleRecheck : controller.handleCheck}
          >
            {failed ? t('cv.health.checkAgain') : t('cv.health.checkThisCv')}
          </button>
        </>
      );
    }
    if (overviewMode === 'stale') {
      // Only reached when the automatic recheck of a changed CV could not start.
      return (
        <>
          <HealthStatusLine text={t('cv.health.cvChanged')} working={false} align="start" />
          <button
            type="button"
            className="tabbio-health-btn is-primary is-block"
            onClick={controller.handleRecheck}
          >
            {t('cv.health.checkAgain')}
          </button>
        </>
      );
    }
    return null;
  }

  const report = overviewReport;
  if (!isReportUsable(report)) {
    return (
      <>
        <HealthStatusLine text={tc(getStageTextKey('failed'))} working={false} align="start" />
        <button
          type="button"
          className="tabbio-health-btn is-primary is-block"
          onClick={controller.handleRecheck}
        >
          {t('cv.health.checkAgain')}
        </button>
      </>
    );
  }

  const fixCount = fixableFindings.length;
  const suggestions = getSuggestionFindings(report);
  return (
    <div className="tabbio-health-report">
      <HealthScoreCard
        report={report}
        historyKey={`editor:${report.targetKey}`}
        action={
          fixCount > 0
            ? {
                label: controller.reviewPending
                  ? tc('cvHealth.report.resumeReview')
                  : tc('cvHealth.report.reviewSuggestions', { count: suggestions.length }),
                onPress: controller.reviewAll,
              }
            : suggestions.length > 0 && controller.recheckStatus !== 'checking'
              ? {
                  // Nothing to change for the user: the suggestions ask for a detail.
                  label: tc('cvHealth.report.reviewSuggestions', { count: suggestions.length }),
                  onPress: () => revealSuggestions(setSuggestionsOpen),
                }
              : null
        }
      />
      <SavedNotice controller={controller} />
      {controller.skippedCount > 0 ? (
        <Note text={tc('cvHealth.heading.skippedFixes', { count: controller.skippedCount })} />
      ) : null}
      {controller.undoFailed ? <Note text={tc('cvHealth.heading.undoFailed')} tone="error" /> : null}
      {suggestions.length > 0 ? (
        <HealthDisclosure
          label={tc('cvHealth.report.viewSuggestions')}
          open={suggestionsOpen}
          onOpenChange={setSuggestionsOpen}
          testID={SUGGESTIONS_TEST_ID}
        >
          <HealthSuggestionList
            report={report}
            findings={suggestions}
            variant="editor"
            {...findingActions(controller)}
            busyFindingKey={controller.busyFindingKey}
          />
        </HealthDisclosure>
      ) : null}
      <HealthDisclosure
        label={tc('cvHealth.report.whatWeChecked')}
        open={checkedOpen}
        onOpenChange={setCheckedOpen}
        testID="cv-health-what-we-checked"
      >
        <HealthCheckedAreas report={report} onOpenArea={controller.openArea} />
      </HealthDisclosure>
    </div>
  );
}

/**
 * What a suggestion row can do. While a saved change rechecks, the rows draw
 * the report that change came from, so they only expand to read.
 */
function findingActions(
  controller: CvHealthSheetController,
): HealthFindingActionProps & { onOpenFinding?: HealthSuggestionListProps['onOpenFinding'] } {
  if (controller.rechecking) return {};
  return {
    onOpenFinding: controller.previewFinding,
    onPreviewChange: controller.previewFinding,
    onDecision: (next, decision) => {
      void controller.decide(next, decision);
    },
    onAddProofLink: controller.addProofLink,
  };
}

function useSaveErrorText(error: CvHealthSaveError | null, skippedCount: number): string | null {
  const { t: tc } = useTranslation('common');
  if (!error) return null;
  if (error === 'skipped') return tc('cvHealth.heading.skippedFixes', { count: skippedCount });
  if (error === 'invalidLink') return tc('cvHealth.review.invalidLink');
  if (error === 'empty') return tc('cvHealth.review.empty');
  return tc('cvHealth.review.saveFailed');
}

function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (node) => node.getClientRects().length > 0 && !node.closest('[inert]'),
  );
}

export function CvHealthSheet({ visible, onClose }: CvHealthSheetProps) {
  const { t } = useTranslation('seeker');
  const { t: tc } = useTranslation('common');
  const isPhone = useIsMobileViewport();
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const wasVisibleRef = useRef(false);

  const confirmDiscard = useCallback(
    () =>
      confirm({
        title: t('cv.health.unsavedTitle'),
        confirmLabel: t('cv.health.discardChanges'),
        cancelLabel: t('cv.health.keepEditing'),
        destructive: true,
      }),
    [t],
  );

  const controller = useCvHealthSheetController({
    visible,
    onClose,
    confirmDiscard,
  });
  const { reset, stepBack, requestClose, view } = controller;

  // Remember the dock control on open; on close, reset the views (there is
  // no slide-out to wait for) and hand focus back to it.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (visible && !wasVisibleRef.current) {
      const active = document.activeElement as HTMLElement | null;
      triggerRef.current = active?.closest<HTMLElement>(DOCK_CONTROL_SELECTOR)
        ?? document.querySelector<HTMLElement>(DOCK_CONTROL_SELECTOR);
    }
    if (!visible && wasVisibleRef.current) {
      reset();
      const trigger = triggerRef.current;
      triggerRef.current = null;
      if (trigger?.isConnected) trigger.focus();
    }
    wasVisibleRef.current = visible;
  }, [reset, visible]);

  // Move focus into the new view so keyboard and screen reader users land on
  // it, unless the view already placed it (Back from an area's tab focuses
  // the "What we checked" row that opened it).
  useEffect(() => {
    if (!visible || !contentNode) return;
    const frame = requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && active !== contentNode && contentNode.contains(active)) return;
      contentNode.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [contentNode, view, visible]);

  // Escape steps back one view (the dialog's own handler would close it
  // outright); Tab stays inside the dialog.
  const stepBackRef = useRef(stepBack);
  stepBackRef.current = stepBack;
  useEffect(() => {
    const dialog = contentNode?.closest<HTMLElement>('[role="dialog"]');
    if (!visible || !dialog) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        void stepBackRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = focusableIn(dialog);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === contentNode)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    dialog.addEventListener('keydown', onKeyDown);
    return () => dialog.removeEventListener('keydown', onKeyDown);
  }, [contentNode, visible]);

  const close = () => {
    void requestClose();
  };

  const { finding } = controller;
  // The header heart works while the checking steps show (Overview's checking branch).
  const checkingShown =
    view === 'overview' &&
    controller.overviewMode === 'checking' &&
    !(controller.showsResult && controller.overviewReport);
  const errorText = useSaveErrorText(controller.saveError, controller.skippedErrorCount);
  // A link that would not validate keeps the link view's own wording.
  const linkError =
    controller.saveError === 'invalidLink'
      ? t('cv.health.invalidLink')
      : controller.saveError === 'saveFailed'
        ? t('cv.health.saveFailed')
        : errorText;

  return (
    <WebDialog
      visible={visible}
      title={t('cv.health.control')}
      titleIcon={<HealthHeart size={20} state={checkingShown ? 'working' : 'resting'} />}
      onClose={close}
      closeLabel={t('cv.health.close')}
      // Design: on a phone the handle is the close control and there is no X;
      // on wider screens a quiet "Done" sits at the trailing edge.
      showCloseButton={!isPhone}
      handleCloseLabel={isPhone ? t('cv.health.close') : undefined}
      compactTitle
      headerTrailing={
        isPhone ? undefined : (
          <button type="button" className="tabbio-health-dialog-done" onClick={close}>
            {t('cv.health.done')}
          </button>
        )
      }
      maxWidth={480}
    >
      <div
        ref={setContentNode}
        tabIndex={-1}
        className="tabbio-health tabbio-health-sheet"
        role={view === 'proposal' ? 'group' : undefined}
        aria-label={view === 'proposal' ? t('cv.health.addLinkTitle') : undefined}
      >
        {view === 'proposal' && finding ? (
          <>
            <button
              type="button"
              className="tabbio-health-back"
              onClick={() => {
                void stepBack();
              }}
            >
              <MaterialSymbol name="chevron_left" size={18} className="tabbio-rtl-flip" />
              {tc('cvHealth.report.overview')}
            </button>
            <HealthProposalReview
              finding={finding}
              onSave={controller.handleApply}
              onKeepOriginal={() => {
                void stepBack();
              }}
              onTextChange={controller.setLinkDraft}
              saving={controller.saving}
              error={linkError}
              saveLabelKey={
                controller.saveError === 'saveFailed' ? 'seeker:cv.health.trySavingAgain' : undefined
              }
            />
          </>
        ) : view === 'review' ? (
          <HealthChangeReview
            items={controller.reviewItems}
            onToggle={controller.toggleDraft}
            onTextChange={controller.setDraftText}
            onApply={() => {
              void controller.saveReview();
            }}
            onKeepOriginal={controller.keepOriginal}
            onBack={() => {
              void stepBack();
            }}
            applyLabel={tc('cvHealth.review.save', { count: controller.selectedCount })}
            saving={controller.saving}
            error={errorText}
            errorFindingKey={controller.errorFindingKey}
            errorNonce={controller.errorNonce}
            otherCount={controller.reviewOtherCount}
            onShowOthers={() => {
              void controller.showOtherSuggestions().then((shown) => {
                if (shown) revealSuggestions(controller.setSuggestionsOpen);
              });
            }}
          />
        ) : view === 'area' && controller.overviewReport ? (
          <HealthAreaReport
            report={controller.overviewReport}
            area={controller.area}
            variant="editor"
            onAreaChange={controller.setArea}
            onBack={controller.backToOverview}
            {...findingActions(controller)}
            onReviewArea={controller.rechecking ? undefined : controller.reviewArea}
            busyFindingKey={controller.busyFindingKey}
          />
        ) : (
          <Overview controller={controller} />
        )}
      </div>
    </WebDialog>
  );
}

export default CvHealthSheet;
